import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDemoModal } from "../contexts/DemoModalContext";
import { useBrochureModal } from "../contexts/BrochureModalContext";
import Flag from "react-world-flags";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaCheckCircle,
  FaDownload,
  FaPhone,
  FaLock,
  FaChevronDown,
  FaCalendarCheck,
} from "react-icons/fa";

const WEB3FORM_ACCESS_KEY = process.env.REACT_APP_WEB3FORM_ACCESS_KEY;

/* ================= BRAND ================= */
const PRIMARY_GRADIENT =
  "bg-gradient-to-r from-primary-soft via-primary to-primary-soft bg-clip-text text-transparent animate-gradient";

/* ================= FAQ DATA ================= */
const FAQ_DATA = [
  {
    question: "Do I need prior knowledge of German to start?",
    answer:
      "No prior knowledge is required for A1 level. Our beginner program starts from scratch, covering pronunciation, basic vocabulary, and everyday expressions. Higher levels require a placement test.",
  },
  {
    question: "What exams does this course prepare me for?",
    answer:
      "Our German programs are aligned with Goethe-Institut standards and prepare students for Goethe-Zertifikat A1–B2 exams, widely accepted for study, work, and immigration.",
  },
  {
    question: "How long does each level take to complete?",
    answer:
      "A1 and A2 typically take 2–3 months, B1 around 3 months, and B2 up to 4 months depending on batch type (weekday or weekend).",
  },
  {
    question: "Are classes online or offline?",
    answer:
      "We offer both online live classes via Zoom and offline classroom sessions at selected locations. Online classes include recordings, live interaction, and practice activities.",
  },
  {
    question: "Will I receive a certificate after completion?",
    answer:
      "Yes, you will receive a course completion certificate from The Lingua Alliance. We also guide you thoroughly for official Goethe certification exams.",
  },
  {
    question: "What if I miss a class?",
    answer:
      "All sessions are recorded and available for lifetime access. You can also attend make-up classes subject to availability.",
  },
  {
    question: "Is German difficult to learn?",
    answer:
      "German grammar is structured and logical. With proper guidance and consistent practice, most learners find it very manageable and rewarding.",
  },
];

/* ================= COMPONENT ================= */
export default function German() {
  const { openModal } = useDemoModal();
  const { openModal: openBrochureModal } = useBrochureModal();
  const formRef = useRef(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!WEB3FORM_ACCESS_KEY) {
      console.error("WEB3FORM_ACCESS_KEY is not configured");
      alert("Form temporarily unavailable. Please try again later.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORM_ACCESS_KEY,
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          subject: 'German Language Program Enrollment',
          from_name: 'The Lingua Alliance - German Program',
          course: 'German Language Program (A1–B2)'
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('Thank you for your interest! We\'ll contact you soon.');
        setFormData({ name: '', email: '', phone: '' });
      } else {
        alert('Failed to submit form. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  /* Initialize AOS */
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      offset: 100,
    });
  }, []);

  /* Sticky stop before footer */
  useEffect(() => {
    const handler = () => {
      const form = formRef.current;
      const footer = document.getElementById("page-footer");
      if (!form || !footer) return;

      const scrollY = window.pageYOffset;
      const footerTop = footer.getBoundingClientRect().top + scrollY;
      const formHeight = form.offsetHeight;
      const stopPoint = footerTop - formHeight - 24;

      if (scrollY >= stopPoint) {
        form.style.position = "absolute";
        form.style.top = `${stopPoint - form.parentElement.offsetTop}px`;
      } else {
        form.style.position = "sticky";
        form.style.top = "24px";
      }
    };

    window.addEventListener("scroll", handler);
    window.addEventListener("resize", handler);
    handler();

    return () => {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 pb-20 lg:pb-0">
      {/* ================= HERO ================= */}
      <header className="relative min-h-[550px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-[center_40%]"
          style={{
            backgroundImage:
              `url(${process.env.PUBLIC_URL}/German_Background.jpg)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/20" />

        <div className="relative max-w-7xl mx-auto px-6 py-16 text-white">
          <div className="max-w-3xl space-y-6">
            <div className="text-sm text-white/80">
              <Link to="/" className="hover:text-white">
                Home
              </Link>{" "}
              / Language Programs / <strong>German</strong>
            </div>

            <div className="flex items-center gap-3">
              <Flag code="DE" style={{ width: 40, height: 28 }} />
              <span className="text-xs uppercase tracking-widest text-white/80">
                Learn German with The Lingua Alliance
              </span>
            </div>

            <h1
              className={`text-4xl md:text-5xl pb-2 font-extrabold text-white`}
            >
              Goethe-Focused German Learning Tracks
            </h1>

            <p className="text-lg text-white/90 max-w-2xl">
              Structured German programs designed to take you from beginner to
              confident, fluent professional with globally recognized standards.
            </p>

            <div className="flex flex-wrap gap-4 pt-3">
              <button
                onClick={() => openBrochureModal("German")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary font-semibold rounded-lg hover:bg-gray-100 transition-all hover:shadow-lg hover:-translate-y-0.5"
              >
                <FaDownload className="text-lg" />
                Download Brochure
              </button>
              <button
                onClick={() => openModal("German")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-soft via-primary to-primary-soft text-white font-semibold rounded-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
              >
                <FaCalendarCheck className="text-lg" />
                Book Free Demo
              </button>
            </div>
          </div>
        </div>
      </header>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="0"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">11+</div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Years of Expertise
                </div>
              </div>
            </div>
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">
                  4,000+
                </div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Students Across Globe
                </div>
              </div>
            </div>
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">✓</div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Certified Experts
                </div>
              </div>
            </div>
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">
                  All Ages
                </div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Programs Available
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= MAIN GRID ================= */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10 md:py-14 grid lg:grid-cols-[1fr_360px] gap-8 sm:gap-10 md:gap-12">
        {/* ================= LEFT CONTENT ================= */}
        <main className="space-y-10 sm:space-y-12 md:space-y-16">
          {/* COURSE LEVELS */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              German Language Program (Goethe Aligned)
            </h2>

            <div className="grid grid-cols-1 gap-4">
              <LevelItem
                title="A1 (Beginner) | 40 Hours"
                text="Begin with the fundamentals: greetings, self-introductions, numbers, pronunciation, and essential everyday vocabulary."
              />
              <LevelItem
                title="A2 (Elementary) | 40 Hours"
                text="Strengthen your foundation using frequently used expressions, basic dialogues, and core grammar concepts."
              />
              <LevelItem
                title="B1 (Intermediate) | 60 Hours"
                text="Engage in conversations about work, travel, and daily life. Understand routine written content and express opinions clearly."
              />
              <LevelItem
                title="B2 (Upper-Intermediate) | 80 Hours"
                text="Participate confidently in detailed discussions, understand complex content, and communicate naturally with native speakers."
              />
            </div>
          </section>

          {/* UNIQUE */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              What Makes Our German Program Unique
            </h2>

            <ul className="grid grid-cols-1 gap-4 text-gray-700">
              <ListItem text="Goethe-aligned curriculum with exam-focused training" />
              <ListItem text="Experienced trainers with real-world usage focus" />
              <ListItem text="Live interactive sessions with recordings" />
              <ListItem text="Structured grammar & speaking practice" />
              <ListItem text="Complete Goethe certification guidance" />
            </ul>
          </section>

          {/* COMPARISON */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              German Language: Difficulty Overview
            </h2>

            <div className="grid grid-cols-1 gap-4">
              <Comparison
                label="Speaking"
                value="Moderate – Improves quickly with practice"
              />
              <Comparison
                label="Grammar"
                value="Structured – Logical and rule-based"
              />
              <Comparison
                label="Writing"
                value="Moderate – Clear sentence construction"
              />
              <Comparison
                label="Overall Difficulty"
                value="Moderate – Highly achievable with guidance"
              />
            </div>
          </section>

          {/* FAQ */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              Frequently Asked Questions
            </h2>

            <div className="space-y-3">
              {FAQ_DATA.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                  >
                    <h4 className="font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h4>
                    <FaChevronDown
                      className={`text-primary transition-transform ${
                        expandedFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {expandedFaq === index && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 border-t border-gray-100">
                      <p className="text-sm text-gray-700">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </main>

        {/* ================= RIGHT STICKY FORM ================= */}
        <aside className="relative">
          <div className="hidden lg:block sticky top-24">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-shadow">
              {/* Header Badge */}
              <div className="text-center mb-5">
                <div className="inline-flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-full mb-4">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                    Limited Cohort | Early Bird Offer Available
                  </span>
                </div>
                <h3
                  className={`text-xl font-bold leading-tight ${PRIMARY_GRADIENT}`}
                >
                  German Language Program <br /> (A1–C2)
                </h3>
                <p className="text-xs text-gray-600 mt-3 leading-relaxed">
                  Live Classes · Goethe / TELC Exam Prep · Study Material
                </p>
              </div>

              {/* Pricing */}
              <div className="text-center mb-6 py-5 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-2xl">🎉</span>
                  <span className="text-2xl font-extrabold text-primary">
                    Flat 30% OFF
                  </span>
                </div>
                <div className="text-sm text-gray-700 font-semibold">
                  on All Levels
                </div>
                <div className="mt-2 text-[11px] text-gray-600 font-medium">
                  Weekdays & Weekend batches
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <input
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg py-3 px-4 outline-none text-sm focus:border-primary focus:bg-white transition"
                  placeholder="Full Name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                />
                <input
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg py-3 px-4 outline-none text-sm focus:border-primary focus:bg-white transition"
                  placeholder="Email Address"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                />
                <input
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg py-3 px-4 outline-none text-sm focus:border-primary focus:bg-white transition"
                  placeholder="Mobile Number"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  required
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-primary-soft via-primary to-primary-soft shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Enroll Now'}
                </button>
              </form>

              {/* Guarantees */}
              <div className="mt-5 text-center">
                <div className="flex justify-center items-center gap-2 text-gray-500">
                  <FaLock className="text-[10px]" />
                  <span className="text-[11px]">
                    Secure & No-obligation Consultation
                  </span>
                </div>
              </div>

              {/* Contact */}
              <div className="mt-6 text-center pt-5 border-t border-gray-100">
                <div className="text-xs text-gray-600 mb-3 font-medium">
                  Prefer talking to a counsellor?
                </div>
                <a
                  href="tel:+919049830590"
                  className="inline-flex items-center gap-2 text-sm bg-blue-50 px-4 py-2.5 rounded-lg text-primary font-semibold border border-blue-200 hover:bg-blue-100 transition"
                >
                  <FaPhone className="text-sm" /> +91-90498 30590
                </a>
              </div>
            </div>
          </div>
        </aside>
      </div>

    </div>
  );
}

/* ================= SMALL COMPONENTS ================= */

function LevelItem({ title, text }) {
  return (
    <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl p-6 shadow-md">
      <h4 className="font-bold mb-3">{title}</h4>
      <p className="text-sm text-gray-700">{text}</p>
    </div>
  );
}

function ListItem({ text }) {
  return (
    <li className="flex gap-2">
      <FaCheckCircle className="text-primary mt-1" />
      {text}
    </li>
  );
}

function Comparison({ label, value }) {
  return (
    <div className="bg-gradient-to-br from-slate-100 to-gray-200 rounded-xl p-6 shadow-md">
      <strong className="block mb-2">{label}</strong>
      <div className="text-sm text-gray-600">{value}</div>
    </div>
  );
}
